import { measureLatency } from '../../../gctl-measure';
import {
  NetworkError,
  deepCamelCase,
  Select,
  deepSnakeCase,
  searchQueryString,
  intoSnakeCase,
} from '../../../fetchers/_helpers';
import {
  PrivateCommentReplyFields,
  PrivateCommentReply,
} from '../../../models/components/schemas/private-comment-reply/PrivateCommentReply';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type DeleteVideoPrivateCommentReplyFields = readonly string[];

/** Options for DELETE /videos/{video_id}/private_comments/{comment_id}/replies/{reply_id} */
export type DeleteVideoPrivateCommentReplyOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the private comment.
     */
    commentId: number;
    /**
     * The ID of the reply.
     */
    replyId: number;
  };

  /** Possible query parameters for /videos/{video_id}/private_comments/{comment_id}/replies/{reply_id} */
  query?: {
    /**
     * The password for the review link.
     */
    password?: string;

    /**
     * The ID of the review link used as context.
     */
    reviewId?: string;
  };
};

/** Result type for DELETE /videos/{video_id}/private_comments/{comment_id}/replies/{reply_id} */
export type DeleteVideoPrivateCommentReplyResult<Paths extends readonly string[]> = unknown;

/**
 * Delete a reply to a private comment
 * This method deletes a reply to the specified private comment.
 */
export async function deleteVideoPrivateCommentReply<
  Paths extends DeleteVideoPrivateCommentReplyFields,
>({
  baseUrl,
  where: { videoId, commentId, replyId },
  query,
  ...options
}: DeleteVideoPrivateCommentReplyOptions<Paths>): Promise<
  DeleteVideoPrivateCommentReplyResult<Paths>
> {
  return measureLatency('deleteVideoPrivateCommentReply', 'DELETE', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}/private_comments/${commentId}/replies/${replyId}?${searchQueryString(
        query,
      )}`,
      {
        ...options,
        method: 'DELETE',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as DeleteVideoPrivateCommentReplyResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PatchVideoPrivateCommentReplyFields = readonly PrivateCommentReplyFields[];

/** Options for PATCH /videos/{video_id}/private_comments/{comment_id}/replies/{reply_id} */
export type PatchVideoPrivateCommentReplyOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the private comment.
     */
    commentId: number;
    /**
     * The ID of the reply.
     */
    replyId: number;
  };

  /** Possible query parameters for /videos/{video_id}/private_comments/{comment_id}/replies/{reply_id} */
  query?: {
    /**
     * The password for the review link.
     */
    password?: string;

    /**
     * The ID of the review link used as context.
     */
    reviewId?: string;
  };

  /** Data serialized to body for PATCH /videos/{video_id}/private_comments/{comment_id}/replies/{reply_id} */
  variables?: {
    /**
     * The rich comment in JSON stringified form.
     * @example {"type":"doc","content":[{"type":"paragraph","content":[{"type":"text","text":"Hi Tom"}]}]}
     */

    richtext?: string;

    /**
     * The reply text.
     * @example Great video!
     */

    text?: string;
  };
};

/** Result type for PATCH /videos/{video_id}/private_comments/{comment_id}/replies/{reply_id} */
export type PatchVideoPrivateCommentReplyResult<Paths extends readonly string[]> = Select<
  PrivateCommentReply,
  Paths
>;

/**
 * Edit a reply to a private comment
 * This method edits the specified reply to a private comment on a video. The authenticated user must be the owner of the reply.
 */
export async function patchVideoPrivateCommentReply<
  Paths extends PatchVideoPrivateCommentReplyFields,
>({
  baseUrl,
  select,
  variables,
  where: { videoId, commentId, replyId },
  query,
  ...options
}: PatchVideoPrivateCommentReplyOptions<Paths>): Promise<
  PatchVideoPrivateCommentReplyResult<Paths>
> {
  return measureLatency('patchVideoPrivateCommentReply', 'PATCH', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}/private_comments/${commentId}/replies/${replyId}?${searchQueryString(
        query,
      )}&fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'PATCH',
        body: JSON.stringify(deepSnakeCase(variables)),
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PatchVideoPrivateCommentReplyResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
